import { GatewayCompany } from "./types";

/**
 * Fetches a gateway's full company → servers → endpoints tree from its own
 * `GET /api/companies` endpoint (the gateway serves it with permissive CORS).
 * Results are cached per hub URL for the session since the catalog is large
 * (MT5 has thousands of companies) and changes only on a 10-minute server TTL.
 */
const cache = new Map<string, Promise<GatewayCompany[]>>();

export function fetchGatewayCompanies(
  hubUrl: string,
  { force = false }: { force?: boolean } = {}
): Promise<GatewayCompany[]> {
  const base = hubUrl.replace(/\/$/, "");
  if (!force && cache.has(base)) return cache.get(base)!;

  const promise = (async () => {
    const res = await fetch(`${base}/api/companies`);
    if (!res.ok) {
      throw new Error(`Failed to load companies (${res.status})`);
    }
    const data = (await res.json()) as GatewayCompany[];
    return Array.isArray(data) ? data : [];
  })();

  // Don't cache rejected fetches — allow a retry on the next open.
  promise.catch(() => cache.delete(base));
  cache.set(base, promise);
  return promise;
}

/**
 * Resolves the company that owns a given broker-server name on a gateway,
 * returning the real company name and best-available logo. Best-effort:
 * returns null when the catalog is unreachable or the server isn't listed.
 */
export async function resolveCompanyForServer(
  hubUrl: string,
  serverName: string
): Promise<{ companyName: string; logoUrl?: string } | null> {
  try {
    const companies = await fetchGatewayCompanies(hubUrl);
    const needle = serverName.toLowerCase();
    for (const c of companies) {
      const sv = c.servers.find((s) => s.name.toLowerCase() === needle);
      if (sv) {
        return {
          companyName: c.company,
          logoUrl: c.logoUrl ?? sv.logoUrl ?? undefined,
        };
      }
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Resolves the company that owns a given broker endpoint (host, optionally port)
 * on a gateway. Used for accounts that stored the raw host as their server name
 * (e.g. the CRM "Open in terminal" connect link). Prefers an exact host:port
 * match, falls back to host-only. Best-effort: null when unreachable/unlisted.
 */
export async function resolveCompanyForEndpoint(
  hubUrl: string,
  host: string,
  port?: number
): Promise<{ companyName: string; serverName: string; logoUrl?: string } | null> {
  try {
    const companies = await fetchGatewayCompanies(hubUrl);
    const needle = host.toLowerCase();
    let hostOnly: { companyName: string; serverName: string; logoUrl?: string } | null = null;
    for (const c of companies) {
      for (const sv of c.servers) {
        for (const ep of sv.endpoints) {
          if (ep.host.toLowerCase() !== needle) continue;
          const hit = {
            companyName: c.company,
            serverName: sv.name,
            logoUrl: c.logoUrl ?? sv.logoUrl ?? undefined,
          };
          if (port === undefined || ep.port === port) return hit;
          hostOnly ??= hit;
        }
      }
    }
    return hostOnly;
  } catch {
    return null;
  }
}
